class TriviaQuizComponent extends HTMLElement {
  constructor() {
    super();
    this.questions = [];
    this.userAnswers = [];
    this.currentQuestionIndex = 0;
    this.score = 0;
    this.quizSessionId = null;
    this.discountCodeData = null;
    this.answerReview = [];
    this.weeklyMode = false;
    this.timerInterval = null;
    this.discountTimerInterval = null;
    this.timeRemaining = 7;
    this.answerLocked = false;
  }

  connectedCallback() {
    const startButton = this.querySelector('[ref="startButton"]');
    if (startButton) startButton.addEventListener('click', () => this.startQuiz());

    const applyButton = this.querySelector('[ref="applyButton"]');
    if (applyButton) applyButton.addEventListener('click', () => this.applyDiscountToCart());

    const emailToggleButton = this.querySelector('[ref="emailToggleButton"]');
    if (emailToggleButton) emailToggleButton.addEventListener('click', () => this.showEmailPanel());

    const emailBackButton = this.querySelector('[ref="emailBackButton"]');
    if (emailBackButton) emailBackButton.addEventListener('click', () => this.hideEmailPanel());

    const emailSendButton = this.querySelector('[ref="emailSendButton"]');
    if (emailSendButton) emailSendButton.addEventListener('click', () => this.sendEmailCode());

    this.checkExistingSession();
  }

  disconnectedCallback() {
    this.clearTimer();
    this.clearDiscountTimer();
  }

  ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  checkExistingSession() {
    const qrId = this.getQRCodeId();
    const sessionId = this.getBrowserSessionId();
    const existingAttempt = sessionStorage.getItem(`quiz_${qrId}_${sessionId}`);

    if (existingAttempt) {
      try {
        const data = JSON.parse(existingAttempt);
        this.showPreviousResults(data);
      } catch (e) {
        // ignore
      }
    }
  }

  getQRCodeId() {
    const questionType = (this.dataset.questionType || 'general').toLowerCase();
    if (questionType === 'weekly') {
      return `WEEKLY-${this.getWeeklyWindowKeyUTC()}`;
    }
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('qr') || 'default';
  }

  getWeeklyWindowKeyUTC() {
    const now = new Date();
    const day = now.getUTCDay();
    const start = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      12, 0, 0, 0
    ));
    const daysSinceFriday = (day + 7 - 5) % 7;
    start.setUTCDate(start.getUTCDate() - daysSinceFriday);
    if (now < start) {
      start.setUTCDate(start.getUTCDate() - 7);
    }
    const y = start.getUTCFullYear();
    const m = String(start.getUTCMonth() + 1).padStart(2, '0');
    const d = String(start.getUTCDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  getBrowserSessionId() {
    let sessionId = sessionStorage.getItem('browser_session_id');
    if (!sessionId) {
      sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      sessionStorage.setItem('browser_session_id', sessionId);
    }
    return sessionId;
  }

  async startQuiz() {
    this.showLoading();

    try {
      const apiEndpoint = this.dataset.apiEndpoint;

      if (!apiEndpoint) {
        throw new Error('API endpoint not configured. Please contact support.');
      }

      const qrId = this.getQRCodeId();
      const sessionId = this.getBrowserSessionId();

      // Category from section setting (data-question-type) or URL param ?category=astronomy
      const urlCategory = new URLSearchParams(window.location.search).get('category');
      const questionType = (urlCategory || this.dataset.questionType || 'general').toLowerCase();
      const response = await fetch(`${apiEndpoint}/api/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code_id: qrId, session_id: sessionId, question_type: questionType })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to start quiz');
      }

      const data = await response.json();
      this.questions = data.questions;
      this.quizSessionId = data.quiz_session_id;
      this.currentQuestionIndex = 0;
      this.userAnswers = [];
      this.score = 0;

      this.hideLoading();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.showQuestion(0);

    } catch (error) {
      console.error('Quiz start error:', error);
      this.showError(error.message);
      this.hideLoading();
    }
  }

  showQuestion(index) {
    this.answerLocked = false;
    window.scrollTo({ top: 0, behavior: 'instant' });
    this.switchView('quiz');

    const question = this.questions[index];
    const questionText = this.ref('questionText');
    const answersContainer = this.ref('answersContainer');
    const questionCounter = this.ref('questionCounter');
    const progressFill = this.ref('progressFill');

    if (questionCounter) questionCounter.textContent = `Question ${index + 1} of ${this.questions.length}`;
    const progress = ((index + 1) / this.questions.length) * 100;
    if (progressFill) progressFill.style.width = `${progress}%`;

    if (questionText) questionText.textContent = question.text;

    if (answersContainer) {
      answersContainer.classList.remove('answers-blocked');
      answersContainer.innerHTML = '';
      const answers = question.type === 'true_false' ? ['True', 'False'] : (question.options || []);
      answers.forEach((answer, i) => {
        const button = document.createElement('button');
        button.className = 'answer-button button';
        button.textContent = answer;
        button.addEventListener('touchstart', () => button.classList.add('pressing'), { passive: true });
        button.addEventListener('touchcancel', () => button.classList.remove('pressing'), { passive: true });
        button.addEventListener('touchend', (e) => {
          e.preventDefault(); // stop the synthetic click from firing
          button.classList.remove('pressing');
          this.selectAnswer(i);
        });
        button.addEventListener('click', () => this.selectAnswer(i));
        answersContainer.appendChild(button);
      });
    }

    this.startTimer();
  }

  startTimer() {
    const timerSeconds = parseInt(this.dataset.timerSeconds) || 5;
    this.timeRemaining = timerSeconds;
    const timerBar = this.ref('timerBar');
    const timerText = this.ref('timerText');

    if (timerBar) {
      timerBar.style.transition = 'none';
      timerBar.style.width = '100%';
      timerBar.offsetWidth; // force reflow
      timerBar.style.transition = `width ${timerSeconds}s linear`;
      timerBar.style.width = '0%';
    }

    if (timerText) timerText.textContent = timerSeconds;

    this.clearTimer();
    this.timerInterval = setInterval(() => {
      this.timeRemaining--;
      if (timerText) timerText.textContent = this.timeRemaining;

      if (this.timeRemaining <= 0) {
        this.clearTimer();
        this.selectAnswer(null);
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  clearDiscountTimer() {
    if (this.discountTimerInterval) {
      clearInterval(this.discountTimerInterval);
      this.discountTimerInterval = null;
    }
  }

  startDiscountCountdown(expiresAt) {
    this.clearDiscountTimer();
    const timerText = this.ref('discountTimerText');
    const timerContainer = this.ref('discountTimer');
    if (!timerText || !timerContainer || !expiresAt) return;

    timerContainer.classList.remove('hidden');

    const updateDisplay = () => {
      const now = new Date().getTime();
      const end = new Date(expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((end - now) / 1000));

      if (remaining <= 0) {
        this.clearDiscountTimer();
        if (timerText) timerText.textContent = 'Discount expired';
        return;
      }

      const mins = Math.floor(remaining / 60);
      const secs = remaining % 60;
      if (timerText) timerText.textContent = `Discount expires in ${mins}:${secs.toString().padStart(2, '0')}`;
    };

    updateDisplay();
    this.discountTimerInterval = setInterval(updateDisplay, 1000);
  }

  selectAnswer(answerIndex) {
    if (this.answerLocked) return;
    this.answerLocked = true;
    this.clearTimer();
    this.userAnswers.push(answerIndex);
    const currentQuestion = this.questions[this.currentQuestionIndex] || {};
    const correctIndex = typeof currentQuestion.correct_index === 'number'
      ? currentQuestion.correct_index
      : -1;

    const buttons = this.querySelectorAll('.answer-button');
    buttons.forEach(btn => {
      btn.disabled = true;
      btn.classList.remove('pressing');
    });

    if (answerIndex !== null && buttons[answerIndex]) {
      if (correctIndex !== -1 && answerIndex !== correctIndex) {
        buttons[answerIndex].classList.add('incorrect');
      } else {
        buttons[answerIndex].classList.add('correct');
      }
    }

    if (correctIndex !== -1 && buttons[correctIndex]) {
      buttons[correctIndex].classList.add('correct');
    }

    if (document.activeElement) document.activeElement.blur();

    setTimeout(() => {
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.showQuestion(this.currentQuestionIndex);
      } else {
        this.submitQuiz();
      }
    }, 900);
  }

  async submitQuiz() {
    this.showLoading();

    try {
      const apiEndpoint = this.dataset.apiEndpoint;

      if (!apiEndpoint) {
        throw new Error('API endpoint not configured');
      }

      const response = await fetch(`${apiEndpoint}/api/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz_session_id: this.quizSessionId,
          answers: this.userAnswers
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to submit quiz');
      }

      const data = await response.json();
      this.score = data.score;
      this.answerReview = Array.isArray(data.answer_review) ? data.answer_review : [];
      this.weeklyMode = Boolean(data.weekly_mode);
      this.discountCodeData = data.discount_code ? {
        code: data.discount_code,
        percentage: data.discount_percentage,
        expires_at: data.expires_at
      } : null;

      // Surface Shopify errors during development so they're visible
      if (data.shopify_error) {
        console.error('Shopify discount error:', data.shopify_error);
      }

      const qrId = this.getQRCodeId();
      const sessionId = this.getBrowserSessionId();
      sessionStorage.setItem(`quiz_${qrId}_${sessionId}`, JSON.stringify({
        score: this.score,
        total: this.questions.length,
        answer_review: this.answerReview,
        discount: this.discountCodeData,
        completed_at: new Date().toISOString()
      }));

      this.hideLoading();
      this.showResults();

    } catch (error) {
      console.error('Quiz submit error:', error);
      this.showError(error.message);
      this.hideLoading();
    }
  }

  showResults() {
    this.switchView('results');

    const scoreValue = this.ref('scoreValue');
    const discountDisplay = this.ref('discountDisplay');
    const noDiscountDisplay = this.ref('noDiscountDisplay');
    const noDiscountMessage = this.ref('noDiscountMessage');
    const discountAmount = this.ref('discountAmount');
    const isWeeklyMode = this.weeklyMode || (this.dataset.questionType || '').toLowerCase() === 'weekly';

    if (scoreValue) scoreValue.textContent = `${this.score}/${this.questions.length}`;

    if (isWeeklyMode) {
      if (discountDisplay) discountDisplay.classList.add('hidden');
      if (noDiscountDisplay) noDiscountDisplay.classList.remove('hidden');
      if (noDiscountMessage) {
        noDiscountMessage.textContent = 'Thanks for playing this week. Come back Friday at noon for the next 7-question trivia.';
      }
    } else if (this.discountCodeData) {
      if (discountDisplay) discountDisplay.classList.remove('hidden');
      if (noDiscountDisplay) noDiscountDisplay.classList.add('hidden');
      if (discountAmount) discountAmount.textContent = `${this.discountCodeData.percentage}% off`;
      // Start countdown timer immediately when results show (discount created on submit)
      if (this.discountCodeData.expires_at) {
        this.startDiscountCountdown(this.discountCodeData.expires_at);
      }
    } else {
      if (discountDisplay) discountDisplay.classList.add('hidden');
      if (noDiscountDisplay) noDiscountDisplay.classList.remove('hidden');
    }
  }

  async applyDiscountToCart() {
    if (!this.discountCodeData) return;

    const applyButton = this.ref('applyButton');
    if (applyButton) {
      applyButton.disabled = true;
      applyButton.textContent = 'Applying...';
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: this.discountCodeData.code })
      });

      if (!response.ok) throw new Error('Failed to apply discount');

      // Save discount info so the countdown banner shows sitewide
      localStorage.setItem('smartass_discount_banner', JSON.stringify({
        code: this.discountCodeData.code,
        percentage: this.discountCodeData.percentage,
        expires_at: this.discountCodeData.expires_at
      }));

      window.location.href = '/collections/all';

    } catch (error) {
      console.error('Discount application error:', error);
      if (applyButton) {
        applyButton.disabled = false;
        applyButton.textContent = 'Shop now with discount';
      }
      this.showError(`Use code: ${this.discountCodeData.code} at checkout`);
    }
  }

  showEmailPanel() {
    const useNowPanel = this.ref('useNowPanel');
    const emailPanel = this.ref('emailPanel');
    if (useNowPanel) useNowPanel.classList.add('hidden');
    if (emailPanel) emailPanel.classList.remove('hidden');
  }

  hideEmailPanel() {
    const useNowPanel = this.ref('useNowPanel');
    const emailPanel = this.ref('emailPanel');
    if (emailPanel) emailPanel.classList.add('hidden');
    if (useNowPanel) useNowPanel.classList.remove('hidden');
  }

  async sendEmailCode() {
    const emailInput = this.ref('emailInput');
    const emailSendButton = this.ref('emailSendButton');
    const emailStatus = this.ref('emailStatus');

    const email = emailInput ? emailInput.value.trim() : '';
    if (!email) {
      this.setEmailStatus('Please enter your email address.', 'error');
      return;
    }

    const apiEndpoint = this.dataset.apiEndpoint;
    if (!apiEndpoint) {
      this.setEmailStatus('Service not configured. Please contact support.', 'error');
      return;
    }

    if (emailSendButton) {
      emailSendButton.disabled = true;
      emailSendButton.textContent = 'Sending...';
    }

    try {
      const response = await fetch(`${apiEndpoint}/api/email-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          quiz_session_id: this.quizSessionId,
          score: this.score,
          answer_review: this.answerReview,
          questions: this.questions,
          answers: this.userAnswers
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send email');
      }

      this.setEmailStatus('Email sent! Check your inbox for your 24-hour discount and question review.', 'success');
      if (emailInput) emailInput.disabled = true;
      if (emailSendButton) emailSendButton.disabled = true;

    } catch (error) {
      console.error('Email send error:', error);
      this.setEmailStatus(error.message || 'Something went wrong. Try again.', 'error');
      if (emailSendButton) {
        emailSendButton.disabled = false;
        emailSendButton.textContent = 'Send Email';
      }
    }
  }

  setEmailStatus(message, type) {
    const emailStatus = this.ref('emailStatus');
    if (!emailStatus) return;
    emailStatus.textContent = message;
    emailStatus.className = `email-status ${type}`;
    emailStatus.classList.remove('hidden');
  }

  switchView(viewName) {
    const landingView = this.ref('landingView');
    const quizView = this.ref('quizView');
    const resultsView = this.ref('resultsView');

    if (landingView) landingView.classList.add('hidden');
    if (quizView) quizView.classList.add('hidden');
    if (resultsView) resultsView.classList.add('hidden');

    if (viewName === 'landing' && landingView) landingView.classList.remove('hidden');
    if (viewName === 'quiz' && quizView) quizView.classList.remove('hidden');
    if (viewName === 'results' && resultsView) resultsView.classList.remove('hidden');
  }

  showLoading() {
    const spinner = this.ref('loadingSpinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  hideLoading() {
    const spinner = this.ref('loadingSpinner');
    if (spinner) spinner.classList.add('hidden');
  }

  showError(message) {
    const errorMessage = this.ref('errorMessage');
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
      setTimeout(() => errorMessage.classList.add('hidden'), 8000);
    }
  }

  showPreviousResults(data) {
    this.score = data.score || 0;
    this.answerReview = Array.isArray(data.answer_review) ? data.answer_review : [];
    this.discountCodeData = data.discount || null;
    this.questions = new Array(data.total || parseInt(this.dataset.questionsPerQuiz) || 5).fill({});
    this.showResults();
    // Restart countdown for previous results
    if (this.discountCodeData?.expires_at) {
      this.startDiscountCountdown(this.discountCodeData.expires_at);
    }
  }
}

if (!customElements.get('trivia-quiz-component')) {
  customElements.define('trivia-quiz-component', TriviaQuizComponent);
}
